import React, { useState, useEffect } from 'react';
import { ShoppingCart, ChevronLeft, ChevronRight, Maximize2, X, Heart } from 'lucide-react';
import { wishlistAPI } from '../services/api';
import { getImgUrl } from '../utils/config';
import { useAuth } from '../context/AuthContext';

const ProductCard = ({ product, onAddToCart, onBuyNow, wishlistItems = [] }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const { user, setShowAuthPopup } = useAuth();

    // Ensure images is an array
    const images = (product.images && product.images.length > 0)
        ? product.images
        : (product.image ? [product.image] : []);

    const hasMultipleImages = images.length > 1;

    // Calculate discount percentage
    const originalPrice = parseFloat(product.originalPrice) || parseFloat(product.price) * 1.2;
    const currentPrice = parseFloat(product.price) || 0;
    const discountPercent = originalPrice > currentPrice
        ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
        : 0;
    const savingsAmount = originalPrice > currentPrice
        ? (originalPrice - currentPrice).toFixed(0)
        : 0;

    // Check if product is in wishlist
    useEffect(() => {
        if (wishlistItems && wishlistItems.length > 0 && product.variationId) {
            const found = wishlistItems.some(item =>
                item.product_variation_id === product.variationId ||
                item.variation?.id === product.variationId
            );
            setIsWishlisted(found);
        }
    }, [wishlistItems, product.variationId]);

    // Auto-slide functionality
    useEffect(() => {
        let interval;
        if (hasMultipleImages && !isHovered && !isModalOpen) {
            interval = setInterval(() => {
                setCurrentImageIndex((prev) => (prev + 1) % images.length);
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [hasMultipleImages, isHovered, isModalOpen, images.length]);

    const nextImage = (e) => {
        e?.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = (e) => {
        e?.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };


    const handleWishlistClick = async (e) => {
        e.stopPropagation();

        if (!user) {
            setShowAuthPopup(true);
            return;
        }

        if (!product.variationId) {
            alert('Product variation not found');
            return;
        }

        setWishlistLoading(true);
        try {
            if (isWishlisted) {
                // Remove from wishlist using product_variation_id
                await wishlistAPI.remove(product.variationId);
                setIsWishlisted(false);
                alert('Removed from wishlist');
            } else {
                // Add to wishlist
                await wishlistAPI.add({
                    product_variation_id: product.variationId
                });
                setIsWishlisted(true);
                alert('Added to wishlist!');
            }
        } catch (error) {
            console.error('Wishlist error:', error);
            const message = error.response?.data?.message || 'Failed to update wishlist';
            if (message === 'Item already in wishlist') {
                setIsWishlisted(true);
            } else {
                alert(message);
            }
        } finally {
            setWishlistLoading(false);
        }
    };

    return (
        <>
            <div
                className="bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Image Container */}
                <div className="relative h-52 bg-gray-50 overflow-hidden">
                    {images.length > 0 ? (
                        <div
                            className="flex transition-transform duration-500 ease-in-out h-full"
                            style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                        >
                            {images.map((img, index) => (
                                <div key={index} className="min-w-full h-full flex items-center justify-center p-4">
                                    <img
                                        src={getImgUrl(img)}
                                        alt={`${product.name} - ${index + 1}`}
                                        className="max-w-full max-h-full object-contain cursor-pointer group-hover:scale-105 transition-transform duration-500"
                                        onClick={() => setIsModalOpen(true)}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                            No Image
                        </div>
                    )}

                    {/* Discount Badge - BigHaat Style */}
                    {discountPercent > 0 && (
                        <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-lg shadow-lg z-20">
                            {discountPercent}% OFF
                        </div>
                    )}

                    {/* Stock Badge */}
                    {product.stock <= 0 && (
                        <div className="absolute top-3 right-3 bg-gray-800 text-white text-xs font-medium px-2 py-1 rounded z-20">
                            Out of Stock
                        </div>
                    )}

                    {/* Wishlist Button */}
                    {product.stock > 0 && (
                        <button
                            className={`absolute top-3 right-3 p-2 rounded-full shadow-md transition-all z-20 ${isWishlisted
                                ? 'bg-red-500 text-white opacity-100'
                                : 'bg-white/90 opacity-0 group-hover:opacity-100 hover:bg-white hover:text-red-500'
                                } ${wishlistLoading ? 'cursor-wait animate-pulse' : ''}`}
                            onClick={handleWishlistClick}
                            disabled={wishlistLoading}
                            title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                        >
                            <Heart size={18} fill={isWishlisted ? 'currentColor' : 'none'} />
                        </button>
                    )}

                    {/* Navigation Arrows */}
                    {hasMultipleImages && (
                        <>
                            <button
                                onClick={prevImage}
                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-md"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <button
                                onClick={nextImage}
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-md"
                            >
                                <ChevronRight size={18} />
                            </button>
                            {/* Dots Indicator */}
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
                                {images.map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-green-600' : 'bg-gray-300'}`}
                                    />
                                ))}
                            </div>
                        </>
                    )}

                    {/* Full Screen Icon */}
                    {images.length > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsModalOpen(true); }}
                            className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-md"
                            title="View Full Screen"
                        >
                            <Maximize2 size={16} />
                        </button>
                    )}
                </div>

                {/* Product Info */}
                <div className="p-2 md:p-4">
                    {/* Category/Brand */}
                    {product.categoryName && (
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{product.categoryName}</p>
                    )}

                    {/* Product Name */}
                    <h3 className="text-sm font-semibold text-gray-800 mb-2 line-clamp-2 min-h-[40px] leading-tight">
                        {product.name}
                    </h3>

                    {/* Price Section - BigHaat Style */}
                    <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-xl font-bold text-gray-900">₹{currentPrice.toLocaleString()}</span>
                        {discountPercent > 0 && (
                            <>
                                <span className="text-sm text-gray-400 line-through">₹{originalPrice.toLocaleString()}</span>
                            </>
                        )}
                    </div>

                    {/* Savings Badge */}
                    {savingsAmount > 0 && (
                        <div className="mb-3">
                            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                                Save ₹{savingsAmount}
                            </span>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                console.log('Add to cart clicked:', product);
                                onAddToCart(product);
                            }}
                            disabled={product.stock <= 0}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${product.stock > 0
                                ? 'bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md'
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            <ShoppingCart size={16} />
                            Add
                        </button>
                        <button
                            onClick={() => onBuyNow(product)}
                            disabled={product.stock <= 0}
                            className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${product.stock > 0
                                ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm hover:shadow-md'
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            Buy Now
                        </button>
                    </div>
                </div>
            </div>

            {/* Full Screen Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}>
                    <button
                        onClick={() => setIsModalOpen(false)}
                        className="absolute top-6 right-6 text-white hover:text-gray-300 z-[110]"
                    >
                        <X size={40} />
                    </button>

                    <div className="relative w-full max-w-5xl h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={getImgUrl(images[currentImageIndex])}
                            alt={product.name}
                            className="max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl"
                        />

                        {hasMultipleImages && (
                            <>
                                <button
                                    onClick={prevImage}
                                    className="absolute left-2 md:left-[-60px] top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 transition-colors"
                                >
                                    <ChevronLeft size={60} />
                                </button>
                                <button
                                    onClick={nextImage}
                                    className="absolute right-2 md:right-[-60px] top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 transition-colors"
                                >
                                    <ChevronRight size={60} />
                                </button>
                            </>
                        )}

                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-3 py-1 rounded-full">
                            {currentImageIndex + 1} / {images.length}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProductCard;
